import React, { useState, useCallback, useEffect } from 'react';
import Landing from './components/Landing';
import Dashboard from './components/Dashboard';
import Login from './components/Login';
import Register from './components/Register';
import SermonTool from './components/SermonTool';
import AnnouncementTool from './components/AnnouncementTool';
import DiscussionTool from './components/DiscussionTool';
import DevotionalTool from './components/DevotionalTool';
import PastoralTool from './components/PastoralTool';
import EvangelismTool from './components/EvangelismTool';
import PrayerTool from './components/PrayerTool';
import SeriesTool from './components/SeriesTool';
import BibleStudyTool from './components/BibleStudyTool';
import EventPromoTool from './components/EventPromoTool';
import History from './components/History';
import ProfilePage from './components/ProfilePage';
import AdminDashboard from './components/AdminDashboard';
import UpgradeModal from './components/UpgradeModal';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import VerifyEmail from './components/VerifyEmail';
import UpgradePage from './components/UpgradePage';
import UpgradeSuccess from './components/UpgradeSuccess';
import InstallPrompt from './components/InstallPrompt';

const TOOL_PAGES = ['sermon','announcement','discussion','devotional','pastoral','evangelism','prayer','series','biblestudy','eventpromo'];

function loadSession() {
  try { return { token: localStorage.getItem('ministry_token'), email: localStorage.getItem('ministry_email'), role: localStorage.getItem('ministry_role') }; }
  catch { return { token: null, email: null, role: null }; }
}
function saveSession(token, email, role) {
  try { localStorage.setItem('ministry_token', token); localStorage.setItem('ministry_email', email||''); localStorage.setItem('ministry_role', role||'free'); } catch {}
}
function clearSession() {
  try { ['ministry_token','ministry_email','ministry_role'].forEach(k => localStorage.removeItem(k)); } catch {}
}

export default function App() {
  const session = loadSession();

  function getInitialPage(hasToken) {
    const path = window.location.pathname;
    const params = new URLSearchParams(window.location.search);
    if (path === '/reset-password' && params.get('token')) return 'reset-password';
    if (path === '/verify-email' && params.get('token')) return 'verify-email';
    if (path === '/upgrade-success') return 'upgrade-success';
    if (path === '/upgrade') return 'upgrade';
    return hasToken ? 'dashboard' : 'landing';
  }

  // Capture referral source from ?ref= URL param on first visit
  (function captureRef() {
    try {
      const ref = new URLSearchParams(window.location.search).get('ref');
      if (ref) localStorage.setItem('ministry_ref', ref.trim().slice(0, 50));
    } catch {}
  })();

  const [page, setPage] = useState(getInitialPage(!!session.token));
  const [token, setToken] = useState(session.token);
  const [userEmail, setUserEmail] = useState(session.email);
  const [userRole, setUserRole] = useState(session.role);
  const [sessionExpiredMsg, setSessionExpiredMsg] = useState('');
  const [userStatus, setUserStatus] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const fetchUserStatus = useCallback(async (tok) => {
    const t = tok || token;
    if (!t) return;
    try {
      const res = await fetch('/api/user/status', { headers: { Authorization: `Bearer ${t}` } });
      if (res.ok) setUserStatus(await res.json());
    } catch {}
  }, [token]);

  useEffect(() => {
    if (token) fetchUserStatus(token);
  }, [token, fetchUserStatus]);

  const handleLogin = useCallback((jwt, email, role) => {
    saveSession(jwt, email, role);
    setToken(jwt); setUserEmail(email); setUserRole(role);
    setSessionExpiredMsg(''); setPage('dashboard');
    fetchUserStatus(jwt);
  }, [fetchUserStatus]);

  const handleLogout = useCallback(() => {
    clearSession(); setToken(null); setUserEmail(null); setUserRole(null);
    setUserStatus(null); setPage('landing');
  }, []);

  const handleAuthExpired = useCallback(() => {
    clearSession(); setToken(null); setUserEmail(null); setUserRole(null);
    setUserStatus(null);
    setSessionExpiredMsg('Your session expired. Please sign in again.'); setPage('login');
  }, []);

  const goTo = useCallback((p) => {
    if (TOOL_PAGES.includes(p) && !token) { setPage('login'); } else { setPage(p); }
  }, [token]);


  const handleUpdateRole = useCallback((newRole, newToken) => {
    setUserRole(newRole);
    try { localStorage.setItem('ministry_role', newRole); } catch {}
    if (newToken) {
      setToken(newToken);
      try { localStorage.setItem('ministry_token', newToken); } catch {}
    }
  }, []);

  const handleUpgradeNeeded = useCallback(() => {
    setShowUpgradeModal(true);
  }, []);

  const handleGenerationComplete = useCallback(() => {
    fetchUserStatus();
  }, [fetchUserStatus]);

  // Shared props for all authenticated pages
  const navProps = { onNavigate: goTo, onLogout: handleLogout, userRole, currentPage: page };
  const genProps = { userStatus, onUpgradeNeeded: handleUpgradeNeeded, onGenerationComplete: handleGenerationComplete };

  if (page === 'landing') return <Landing onGetStarted={() => goTo('login')} onLogin={() => setPage('login')} />;
  if (page === 'login') return <Login onLogin={handleLogin} onRegister={() => setPage('register')} onNavigate={goTo} sessionExpiredMsg={sessionExpiredMsg} />;
  if (page === 'register') return <Register onLoginLink={() => setPage('login')} />;
  if (page === 'forgot-password') return <ForgotPassword onNavigate={goTo} />;
  if (page === 'reset-password') return <ResetPassword onNavigate={goTo} />;
  if (page === 'verify-email') return <VerifyEmail onNavigate={goTo} />;

  const toolProps = { ...navProps, ...genProps, token, onAuthExpired: handleAuthExpired };

  return (
    <>
      {showUpgradeModal && (
        <UpgradeModal
          onDismiss={() => setShowUpgradeModal(false)}
          onNavigate={goTo}
        />
      )}
      {page === 'dashboard' && <Dashboard {...navProps} {...genProps} userEmail={userEmail} onSelectTool={goTo} />}
      {page === 'history' && <History {...navProps} token={token} onAuthExpired={handleAuthExpired} />}
      {page === 'profile' && <ProfilePage {...navProps} token={token} onAuthExpired={handleAuthExpired} />}
      {page === 'admin' && <AdminDashboard {...navProps} token={token} onAuthExpired={handleAuthExpired} />}
      {page === 'upgrade' && <UpgradePage token={token} onNavigate={goTo} onLogout={handleLogout} userRole={userRole} currentPage={page} />}
      {page === 'upgrade-success' && <UpgradeSuccess token={token} onNavigate={goTo} onUpdateRole={handleUpdateRole} />}
      {page === 'sermon' && <SermonTool {...toolProps} />}
      {page === 'announcement' && <AnnouncementTool {...toolProps} />}
      {page === 'discussion' && <DiscussionTool {...toolProps} />}
      {page === 'devotional' && <DevotionalTool {...toolProps} />}
      {page === 'pastoral' && <PastoralTool {...toolProps} />}
      {page === 'evangelism' && <EvangelismTool {...toolProps} />}
      {page === 'prayer' && <PrayerTool {...toolProps} />}
      {page === 'series' && <SeriesTool {...toolProps} />}
      {page === 'biblestudy' && <BibleStudyTool {...toolProps} />}
      {page === 'eventpromo' && <EventPromoTool {...toolProps} />}
      {!['landing','login','register','forgot-password','reset-password','verify-email','dashboard','history','profile','admin','upgrade','upgrade-success',...TOOL_PAGES].includes(page) && (
        <Dashboard {...navProps} {...genProps} userEmail={userEmail} onSelectTool={goTo} />
      )}
      <InstallPrompt />
    </>
  );
}
